import { Controller } from 'react-hook-form';
import { MenuItem, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { FormDialog } from '../../../shared/components/FormDialog';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import { LookupViaCepButton } from '../../../shared/components/LookupViaCepButton';
import { UFS } from '../../../shared/data/brasil';
import { companyAddressInputSchema, type CompanyAddressFormValues } from './schemas';
import type { CepLookupResult, CompanyAddressDto, CompanyAddressInput } from './types';

export interface CompanyAddressFormDialogProps {
  open: boolean;
  address: CompanyAddressDto | null;
  hooks: {
    useCreate: () => UseMutationResult<CompanyAddressDto, Error, CompanyAddressInput>;
    useUpdate: () => UseMutationResult<
      CompanyAddressDto,
      Error,
      { id: number; input: CompanyAddressInput }
    >;
    useLookupCep: () => UseMutationResult<CepLookupResult, Error, string>;
  };
  onClose: () => void;
}

const DEFAULT_VALUES: CompanyAddressFormValues = {
  cep: '',
  logradouro: '',
  numero: '',
  complemento: null,
  bairro: '',
  cidade: '',
  uf: '',
  ibge: '',
  ativo: true,
};

export function CompanyAddressFormDialog({
  open,
  address,
  hooks,
  onClose,
}: CompanyAddressFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const lookupMut = hooks.useLookupCep();

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    CompanyAddressFormValues,
    CompanyAddressDto,
    CompanyAddressInput,
    number
  >({
    open,
    entity: address,
    schema: companyAddressInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (a) => ({
      cep: a.cep,
      logradouro: a.logradouro,
      numero: a.numero,
      complemento: a.complemento,
      bairro: a.bairro,
      cidade: a.cidade,
      uf: a.uf,
      ibge: a.ibge,
      ativo: a.ativo,
    }),
    getId: (a) => a.id,
    hooks: { useCreate: hooks.useCreate, useUpdate: hooks.useUpdate },
    messages: {
      created: t('features.empresa.enderecos.toasts.created'),
      updated: t('features.empresa.enderecos.toasts.updated'),
    },
    focusField: 'cep',
    onClose,
  });

  const handleLookup = async (cepDigits: string): Promise<void> => {
    const r = await lookupMut.mutateAsync(cepDigits);
    form.setValue('logradouro', r.logradouro, { shouldValidate: true });
    form.setValue('bairro', r.bairro, { shouldValidate: true });
    form.setValue('cidade', r.cidade, { shouldValidate: true });
    form.setValue('uf', r.uf, { shouldValidate: true });
    form.setValue('ibge', r.ibge, { shouldValidate: true });
  };

  return (
    <FormDialog
      open={open}
      title={
        isEdit
          ? t('features.empresa.enderecos.editTitle')
          : t('features.empresa.enderecos.createTitle')
      }
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="sm"
    >
      <Stack spacing={2}>
        <Controller
          control={form.control}
          name="cep"
          render={({ field, fieldState }) => (
            <LookupViaCepButton
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              inputRef={field.ref}
              onLookup={handleLookup}
              loading={lookupMut.isPending}
              error={fieldState.error?.message}
            />
          )}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {/*
            Campos preenchidos pelo ViaCEP (logradouro/bairro/cidade/ibge) usam
            `Controller` em vez de `register`: com `register` o TextField fica
            uncontrolled e o `setValue` do lookup escreve no DOM sem notificar o
            MUI, então o label não sobe (`shrink`) e sobrepõe o texto.
          */}
          <Controller
            control={form.control}
            name="logradouro"
            render={({ field, fieldState }) => (
              <TextField
                label={t('features.empresa.enderecos.fields.logradouro')}
                fullWidth
                inputProps={{ maxLength: 150 }}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <TextField
            label={t('features.empresa.enderecos.fields.numero')}
            sx={{ maxWidth: 140 }}
            inputProps={{ maxLength: 20 }}
            error={Boolean(form.formState.errors.numero)}
            helperText={form.formState.errors.numero?.message}
            {...form.register('numero')}
          />
        </Stack>

        <TextField
          label={t('features.empresa.enderecos.fields.complemento')}
          fullWidth
          inputProps={{ maxLength: 100 }}
          error={Boolean(form.formState.errors.complemento)}
          helperText={form.formState.errors.complemento?.message}
          {...form.register('complemento')}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            control={form.control}
            name="bairro"
            render={({ field, fieldState }) => (
              <TextField
                label={t('features.empresa.enderecos.fields.bairro')}
                fullWidth
                inputProps={{ maxLength: 100 }}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={form.control}
            name="cidade"
            render={({ field, fieldState }) => (
              <TextField
                label={t('features.empresa.enderecos.fields.cidade')}
                fullWidth
                inputProps={{ maxLength: 100 }}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            control={form.control}
            name="uf"
            render={({ field, fieldState }) => (
              <TextField
                select
                label={t('features.empresa.enderecos.fields.uf')}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="">—</MenuItem>
                {UFS.map((uf) => (
                  <MenuItem key={uf} value={uf}>
                    {uf}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            control={form.control}
            name="ibge"
            render={({ field, fieldState }) => (
              <TextField
                label={t('features.empresa.enderecos.fields.ibge')}
                sx={{ maxWidth: 160 }}
                inputProps={{ maxLength: 7 }}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ?? t('features.empresa.enderecos.fields.ibgeHelper')
                }
              />
            )}
          />
        </Stack>
      </Stack>
    </FormDialog>
  );
}
